import React, { useState, useEffect } from 'react';
import { Clock, Save, Info, Loader2 } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import { logUsageEvent } from '../../src/lib/logger';
import { ConfiguracaoSLA } from '../../types';

export default function SlaSettings() {
    const { addToast } = useToast();
    const { user } = useAuth();
    const [slas, setSlas] = useState<ConfiguracaoSLA[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchSlas();
    }, []);

    const fetchSlas = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('configuracoes_sla')
                .select('*')
                .order('nome');

            if (error) throw error;
            setSlas(data || []);
        } catch (error: any) {
            console.error('Error fetching SLAs:', error);
            addToast('Erro ao carregar configurações de SLA.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSlaChange = (id: string, hours: number) => {
        setSlas(prev => prev.map(sla =>
            sla.id === id ? { ...sla, tempo_horas: hours } : sla
        ));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            
            for (const sla of slas) {
                const { error } = await supabase
                    .from('configuracoes_sla')
                    .update({ tempo_horas: sla.tempo_horas })
                    .eq('id', sla.id);
                
                if (error) throw error;
            }

            addToast('Configurações de SLA salvas com sucesso!', 'success');
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'slas', acao: 'salvar_configuracao_sla', origem: 'acao' });
        } catch (error: any) {
            console.error('Error saving SLAs:', error);
            addToast('Erro ao salvar as configurações de SLA.', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
        );
    }

    return (
        <div className="animate-fade-in pb-12">
            <div className="mb-8">
                <h1 className="text-2xl font-bold font-poppins text-gray-900 dark:text-white mb-2">SLA / Prazos (Deadlines)</h1>
                <p className="text-gray-500 dark:text-gray-400">
                    Configure o tempo limite esperado para cada etapa ou ação no VigorHub. Um cronômetro será exibido e destacará atrasos visualmente.
                </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden mb-8">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <Clock size={20} className="text-rose-500" />
                            Prazos de Execução
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Defina os prazos máximos em horas.
                        </p>
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-6 py-2.5 rounded-xl font-bold transition-all shadow-sm shadow-primary/20 disabled:opacity-50"
                    >
                        {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                        Salvar Prazos
                    </button>
                </div>

                <div className="p-6">
                    {slas.length === 0 ? (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                            Nenhuma configuração de SLA encontrada no banco de dados.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {slas.map(sla => (
                                <div key={sla.id} className="p-5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 flex flex-col justify-between">
                                    <div className="mb-4">
                                        <h3 className="font-bold text-gray-900 dark:text-white text-lg">{sla.nome}</h3>
                                        {sla.tipo === 'analise_cadastral' && (
                                            <p className="text-xs text-gray-500 flex items-start gap-1 mt-2">
                                                <Info size={14} className="shrink-0 mt-0.5 text-blue-500" />
                                                O timer inicia assim que a negociação entra no status "Termo em análise".
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3 mt-auto pt-4 border-t border-gray-100 dark:border-gray-700">
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 w-24">Prazo (horas):</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={sla.tempo_horas}
                                            onChange={(e) => handleSlaChange(sla.id, parseInt(e.target.value) || 0)}
                                            className="flex-1 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2 font-bold focus:border-primary outline-none transition-colors"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
